import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isPremium } from "../lib/premium";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Upload, FileSpreadsheet, Loader2, Check, ImageOff, Sparkles, AlertCircle, Crown,
} from "lucide-react";

const ACCEPTED = ".csv,.tsv,.xlsx,.xls";

function PremiumGate() {
  const navigate = useNavigate();
  return (
    <div data-testid="bulk-premium-gate" className="bg-gradient-to-br from-amber-950/30 via-zinc-900 to-zinc-900 border border-amber-500/20 rounded-2xl p-8 text-center max-w-2xl mx-auto">
      <Crown className="w-10 h-10 text-amber-400 mx-auto mb-4" />
      <h2 className="font-heading text-2xl font-bold text-zinc-100">Bulk import is a Premium feature</h2>
      <p className="text-zinc-400 mt-2">Bring in your existing spreadsheet — CSV or Excel, up to 1,000 rows — with column auto-mapping and smart catalog matching.</p>
      <Button onClick={() => navigate("/billing")} className="mt-6 bg-amber-400 hover:bg-amber-300 text-black font-semibold" data-testid="bulk-upgrade-btn">
        <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Premium
      </Button>
    </div>
  );
}

function MatchedRow({ item, onToggle, onSetCatalog }) {
  const m = item.match;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/60 ring-1 ring-emerald-500/10" data-testid={`bulk-row-matched-${item.row_index}`}>
      <Checkbox
        checked={!item._skip}
        onCheckedChange={(v) => onToggle(item.row_index, !v)}
        data-testid={`bulk-row-toggle-${item.row_index}`}
      />
      {m.model_image ? (
        <img src={resolveImageUrl(m.model_image)} alt="" className="w-12 h-12 rounded object-contain bg-zinc-800" />
      ) : (
        <div className="w-12 h-12 rounded bg-zinc-800 flex items-center justify-center"><ImageOff className="w-5 h-5 text-zinc-600" /></div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-zinc-100 text-sm font-medium truncate">{m.release_name}</p>
        <p className="text-zinc-500 text-xs">{m.brand} · {m.vehicle} · {m.khmg_number}</p>
      </div>
      <div className="text-right">
        <span className="text-emerald-400 text-xs font-semibold flex items-center gap-1 justify-end">
          <Check className="w-3 h-3" /> {Math.round((item.match_score || 0) * 100)}% match
        </span>
        <p className="text-zinc-500 text-xs mt-1">qty {item.row.quantity || 1}</p>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onSetCatalog(item.row_index, null)} className="text-zinc-500 hover:text-rose-400 text-xs" data-testid={`bulk-row-unmatch-${item.row_index}`}>
        Unmatch
      </Button>
    </div>
  );
}

function UnmatchedRow({ item, onToggle }) {
  const r = item.row;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/60 ring-1 ring-amber-500/10" data-testid={`bulk-row-unmatched-${item.row_index}`}>
      <Checkbox
        checked={!item._skip}
        onCheckedChange={(v) => onToggle(item.row_index, !v)}
        data-testid={`bulk-row-toggle-${item.row_index}`}
      />
      <div className="w-12 h-12 rounded bg-amber-500/10 flex items-center justify-center">
        <AlertCircle className="w-5 h-5 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-zinc-100 text-sm font-medium truncate">{r.release_name || "(no name)"}</p>
        <p className="text-zinc-500 text-xs">{[r.brand, r.vehicle, r.khmg_number].filter(Boolean).join(" · ") || "Will import as custom item"}</p>
      </div>
      <p className="text-zinc-500 text-xs">qty {r.quantity || 1}</p>
    </div>
  );
}

export default function BulkImportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("upload"); // upload | preview | done
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [skips, setSkips] = useState({}); // { row_index: true }
  const [unmatchAsCustom, setUnmatchAsCustom] = useState({}); // { row_index: true }
  const [result, setResult] = useState(null);

  if (!isPremium(user)) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="bulk-import-page">
        <PremiumGate />
      </div>
    );
  }

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bulk-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setSkips({});
      setUnmatchAsCustom({});
      setPhase("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleSkip = (rowIndex, skip) => {
    setSkips((prev) => ({ ...prev, [rowIndex]: skip }));
  };

  const setCatalogMatch = (rowIndex, releaseId) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const fromMatched = next.matched.find((m) => m.row_index === rowIndex);
      if (fromMatched && releaseId === null) {
        // Move matched → unmatched (user rejected match)
        next.matched = next.matched.filter((m) => m.row_index !== rowIndex);
        next.unmatched = [...next.unmatched, { ...fromMatched, match: null }];
        next.matched_count = next.matched.length;
        next.unmatched_count = next.unmatched.length;
      }
      return next;
    });
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    const items = [
      ...preview.matched.map((m) => ({
        row_index: m.row_index,
        row: m.row,
        release_id: m.match.release_id,
        skip: !!skips[m.row_index],
      })),
      ...preview.unmatched.map((u) => ({
        row_index: u.row_index,
        row: u.row,
        release_id: null,
        skip: !!skips[u.row_index],
      })),
    ];
    try {
      const { data } = await api.post("/bulk-import/commit", { items });
      setResult(data);
      setPhase("done");
      const total = (data.catalog_inserted || 0) + (data.custom_inserted || 0);
      toast.success(`Imported ${total} items`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bulk-import-page" className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6 animate-fadeIn">
      <div>
        <p className="text-amber-400 text-xs uppercase tracking-[0.3em] font-semibold flex items-center gap-2">
          <Crown className="w-3.5 h-3.5" /> Premium · Bulk Import
        </p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-100 mt-2">Import your spreadsheet</h1>
        <p className="text-zinc-400 mt-1">CSV or Excel · up to 1,000 rows · auto-mapping for common column names.</p>
      </div>

      {phase === "upload" && (
        <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl p-8 sm:p-12 text-center">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            data-testid="bulk-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <FileSpreadsheet className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-zinc-100 text-lg font-medium">Drop your file here or pick from device</p>
          <p className="text-zinc-500 text-sm mt-1">Accepts: .csv, .tsv, .xlsx, .xls</p>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="mt-6 bg-amber-400 hover:bg-amber-300 text-black font-semibold"
            data-testid="bulk-pick-file-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Pick a file
          </Button>
          <div className="text-zinc-500 text-xs mt-6">
            Recognised columns: Release Name, Catalog #, Brand, Vehicle, Series, Quantity, Purchase Price, Date, Seller, Condition, Notes…
          </div>
        </div>
      )}

      {phase === "preview" && preview && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-900 rounded-xl p-4 ring-1 ring-white/5">
              <p className="text-zinc-500 text-xs uppercase tracking-widest">Total rows</p>
              <p className="text-3xl font-black text-white mt-1">{preview.total_rows}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 ring-1 ring-emerald-500/10">
              <p className="text-emerald-400 text-xs uppercase tracking-widest">Matched</p>
              <p className="text-3xl font-black text-emerald-400 mt-1">{preview.matched_count}</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 ring-1 ring-amber-500/10">
              <p className="text-amber-400 text-xs uppercase tracking-widest">Custom</p>
              <p className="text-3xl font-black text-amber-400 mt-1">{preview.unmatched_count}</p>
            </div>
          </div>

          {preview.matched.length > 0 && (
            <div>
              <h3 className="text-zinc-200 font-semibold text-sm mb-2">Catalog matches</h3>
              <div className="space-y-2">
                {preview.matched.map((it) => (
                  <MatchedRow
                    key={it.row_index}
                    item={{ ...it, _skip: !!skips[it.row_index] }}
                    onToggle={toggleSkip}
                    onSetCatalog={setCatalogMatch}
                  />
                ))}
              </div>
            </div>
          )}

          {preview.unmatched.length > 0 && (
            <div>
              <h3 className="text-zinc-200 font-semibold text-sm mb-2">Will be imported as custom items</h3>
              <div className="space-y-2">
                {preview.unmatched.map((it) => (
                  <UnmatchedRow
                    key={it.row_index}
                    item={{ ...it, _skip: !!skips[it.row_index] }}
                    onToggle={toggleSkip}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" onClick={() => setPhase("upload")} data-testid="bulk-back-btn">
              ← Different file
            </Button>
            <Button
              onClick={commit}
              disabled={busy}
              className="bg-amber-400 hover:bg-amber-300 text-black font-semibold"
              data-testid="bulk-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirm import
            </Button>
          </div>
        </div>
      )}

      {phase === "done" && result && (
        <div className="bg-zinc-900 ring-1 ring-emerald-500/20 rounded-2xl p-8 text-center" data-testid="bulk-done-card">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-emerald-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-zinc-100">Import complete</h2>
          <p className="text-zinc-400 mt-2">
            {result.catalog_inserted} catalog · {result.custom_inserted} custom · {result.skipped} skipped
            {result.errors?.length ? ` · ${result.errors.length} errors` : ""}
          </p>
          <div className="flex justify-center gap-3 mt-6">
            <Button variant="outline" onClick={() => { setPhase("upload"); setPreview(null); setResult(null); }} data-testid="bulk-import-another-btn">
              Import another
            </Button>
            <Button onClick={() => navigate("/collection")} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="bulk-view-collection-btn">
              View Collection
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
